import { TimingInfo } from "./MasterComposer";

/**
 * Structure definition for a musical phrase including its fill.
 */
export interface PhraseStructure {
  /** Total length of the phrase in beats (8, 16, 24, or 32) */
  phraseLength: 8 | 16 | 24 | 32;
  /** Length of the fill at the end of the phrase (2, 4, or 8) */
  fillLength: 2 | 4 | 8;
  /** Type of fill for coordination between instruments */
  fillType: "small" | "medium" | "large";
}

/**
 * Manages musical phrase structure and timing information.
 *
 * Tracks complete musical phrases with integrated fills at the end,
 * where fills are the natural conclusion of each phrase. Provides
 * timing information for pattern selection and execution.
 *
 * Musical structure:
 * - Phrases are 8, 16, 24, or 32 beats long
 * - Each phrase ends with a 2, 4, or 8 beat fill
 * - Fills are typed (small/medium/large) for coordinated dynamics
 * - Deterministic phrase seeds ensure consistent pattern selection
 */
export class PhraseManager {
  /** Fixed phrase cycle that repeats */
  private readonly phraseStructures: PhraseStructure[] = [
    { phraseLength: 16, fillLength: 2, fillType: "small" },
    { phraseLength: 16, fillLength: 4, fillType: "medium" },
    { phraseLength: 16, fillLength: 2, fillType: "small" },
    { phraseLength: 32, fillLength: 8, fillType: "large" },
  ];

  /** Total beats in the complete cycle */
  private readonly totalCycleBeats: number;

  constructor() {
    // Calculate total cycle length
    this.totalCycleBeats = this.phraseStructures.reduce(
      (sum, phrase) => sum + phrase.phraseLength,
      0,
    );
  }

  /**
   * Get timing information for the given beat.
   * Calculates phrase position and fill status.
   */
  getTimingInfo(beatNumber: number): TimingInfo {
    // Find which phrase we're in
    const beatInCycle = beatNumber % this.totalCycleBeats;
    let cumulativeBeats = 0;
    let phraseIndex = 0;

    for (let i = 0; i < this.phraseStructures.length; i++) {
      const phrase = this.phraseStructures[i];
      if (beatInCycle < cumulativeBeats + phrase.phraseLength) {
        phraseIndex = i;
        break;
      }
      cumulativeBeats += phrase.phraseLength;
    }

    const currentPhrase = this.phraseStructures[phraseIndex];
    const beatInPhrase = beatInCycle - cumulativeBeats;

    return {
      beat: beatNumber,
      beatInPhrase,
      phraseLength: currentPhrase.phraseLength,
      fillLength: currentPhrase.fillLength,
      fillType: currentPhrase.fillType,
      phraseSeed: this.generatePhraseSeed(beatNumber, phraseIndex),
    };
  }

  /**
   * Get the structure of the next phrase for anticipation.
   * Returns null if we're not near the end of current phrase.
   */
  getNextPhraseStructure(beatNumber: number): PhraseStructure | null {
    const timingInfo = this.getTimingInfo(beatNumber);

    // Only provide next phrase info in the last 4 beats of current phrase
    if (timingInfo.beatInPhrase < timingInfo.phraseLength - 4) {
      return null;
    }

    // Find next phrase index
    const beatInCycle = beatNumber % this.totalCycleBeats;
    let cumulativeBeats = 0;
    let currentPhraseIndex = 0;

    for (let i = 0; i < this.phraseStructures.length; i++) {
      const phrase = this.phraseStructures[i];
      if (beatInCycle < cumulativeBeats + phrase.phraseLength) {
        currentPhraseIndex = i;
        break;
      }
      cumulativeBeats += phrase.phraseLength;
    }

    const nextPhraseIndex =
      (currentPhraseIndex + 1) % this.phraseStructures.length;
    return this.phraseStructures[nextPhraseIndex];
  }

  /**
   * Generate a deterministic seed for the phrase.
   * Ensures same phrase always gets same seed for consistent patterns.
   */
  private generatePhraseSeed(beatNumber: number, phraseIndex: number): number {
    // Which complete cycle are we in?
    const cycleNumber = Math.floor(beatNumber / this.totalCycleBeats);
    // Combine cycle and phrase index for unique seed
    return cycleNumber * 1000 + phraseIndex;
  }

  /**
   * Helper to check if we're currently in a fill section.
   */
  static isInFill(timingInfo: TimingInfo): boolean {
    return (
      timingInfo.beatInPhrase >= timingInfo.phraseLength - timingInfo.fillLength
    );
  }

  /**
   * Helper to get beat position within the fill (0-based).
   * Returns -1 if not in a fill.
   */
  static getBeatInFill(timingInfo: TimingInfo): number {
    if (!this.isInFill(timingInfo)) {
      return -1;
    }
    return (
      timingInfo.beatInPhrase -
      (timingInfo.phraseLength - timingInfo.fillLength)
    );
  }
}
